import React, { useEffect, useState } from 'react';
import { TabType } from 'types/TabTypes';
import { useTranslation } from 'react-i18next';
import Timeline from 'components/timeline/Timeline';
import PdfLayoutExample from '../../views/settings/pdf/PdfLayoutExample';
import JobDetailList from '../../views/jobs/JobDetailList';
import AttachmentDetailList from '../../views/attachment/AttachmentDetailList';
import ProposalDetailList from '../../views/proposals/ProposalDetailList';
import InvoiceDetailList from '../../views/invoices/InvoiceDetailList';
import TicketDetailList from '../../views/tickets/TicketDetailList';
import ProductDescriptionTab from '../../views/products/ProductDescriptionTab';
import ProductPriceMatrixTab from '../../views/products/ProductPriceMatrixTab';
import EmailLayoutExample from '../../views/settings/email/EmailLayoutExample';
import CustomFieldsLiveEdit from 'components/forms/custom_fields/CustomFieldLiveEdit';
import EmployeeTimeTableTab from '../../views/employees/EmployeeTimeTableTab';
import ContractJobSchedulesTab from '../../views/contracts/ContractJobSchedulesTab';
import ContractInvoicesTab from '../../views/contracts/ContractInvoicesTab';

interface TabBarProps {
    tabs: TabType[];
    data?: any;
    viewKey?: string;
    itemId?: string;
    pageContext?: string;
    reloadTabs?: number;
    refetchData?: () => void;
}

const TabBar: React.FC<TabBarProps> = ({ tabs, data, viewKey, itemId, pageContext, reloadTabs, refetchData }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);

    // Sets the first tab to active
    useEffect(() => {
        if (setActiveTab && typeof activeTab === 'undefined') {
            setActiveTab(0);
        }
    }, [setActiveTab, activeTab]);

    // Only keep the tabs whose condition on the detail page data holds
    const visibleTabs = tabs.filter(tab => {
        if (!tab.condition) return true;
        return tab.condition.every(condition => condition.values.includes(data?.[condition.field]));
    });

    // Fall back to the first tab when the active tab is hidden by its condition
    useEffect(() => {
        if (activeTab >= visibleTabs.length) {
            setActiveTab(0);
        }
    }, [activeTab, visibleTabs.length]);

    // Generates the tab components
    const tabComponents = visibleTabs.map((tab, index) => {
        let component = tab.component;
        if (!component && tab.type && viewKey) {
            switch(tab.type) {
                case 'timeline':
                    component = itemId && pageContext ? 
                        <Timeline 
                            key={index} 
                            placement='page'
                            timeline_page={pageContext || ''} 
                            timeline_page_id={parseInt(itemId)} 
                            showOnlyTimelineUpdates={tab.showOnlyTimelineUpdates}
                            data={data}
                        /> 
                        : null;
                    break;
                case 'proposals': 
                    component = itemId && pageContext ? 
                        <ProposalDetailList 
                            key={index} 
                            itemId={itemId} 
                            detailPage={pageContext} 
                            data={data}
                        /> 
                        : null;
                    break;
                case 'jobs': 
                    component = itemId && pageContext ? 
                        <JobDetailList 
                            key={index} 
                            itemId={itemId} 
                            detailPage={pageContext} 
                            data={data} /> 
                        : null;
                    break;
                case 'invoices': 
                    component = itemId && pageContext ? 
                        <InvoiceDetailList
                            key={index} 
                            itemId={itemId} 
                            detailPage={pageContext} 
                            data={data} /> 
                        : null;
                    break;
                case 'tickets':
                    component = itemId && pageContext ?
                        <TicketDetailList
                            key={index}
                            itemId={itemId}
                            detailPage={pageContext}
                            data={data} />
                        : null;
                    break;
                case 'pdf-layout':
                    component = itemId ? 
                        <PdfLayoutExample 
                            itemId={itemId} 
                            reloadPdf={reloadTabs ? reloadTabs : null} 
                        /> 
                        : null;
                    break;
                case 'email-layout':
                    component = itemId ? 
                        <EmailLayoutExample 
                            layoutId={Number(itemId)} 
                            reloadLayout={reloadTabs ?? null}
                        /> 
                        : null;
                    break;
                case 'attachments':
                    component = itemId && pageContext ? 
                        <AttachmentDetailList 
                            key={index} 
                            detailPageId={itemId} 
                            detailPage={pageContext} 
                        /> 
                        : null;
                    break;
                case 'product-description':
                    component = itemId ?
                        <ProductDescriptionTab
                            key={index}
                            viewKey={viewKey}
                            itemId={itemId}
                            data={data}
                            refetchData={refetchData}
                        />
                        : null;
                    break;
                case 'product-price-matrix':
                    component = itemId ?
                        <ProductPriceMatrixTab
                            key={index}
                            itemId={itemId}
                            data={data}
                            refetchData={refetchData}
                        />
                        : null;
                    break;
                case 'timetable':
                    component = itemId ?
                        <EmployeeTimeTableTab
                            key={index}
                            itemId={itemId}
                        />
                        : null;
                    break;
                case 'contract-job-schedules':
                    component = itemId ?
                        <ContractJobSchedulesTab
                            key={index}
                            itemId={itemId}
                            data={data}
                        />
                        : null;
                    break;
                case 'contract-invoices':
                    component = itemId ?
                        <ContractInvoicesTab
                            key={index}
                            itemId={itemId}
                            data={data}
                            refetchData={refetchData}
                        />
                        : null;
                    break;
                case 'information':
                    component = itemId && pageContext ? (
                        // Only render the custom fields if they exist
                        Array.isArray(data?.custom_fields) && data.custom_fields.length > 0 ? (
                            <CustomFieldsLiveEdit
                                key={index}
                                data={data}
                                itemId={itemId}
                                apiObject={pageContext}
                                layout='multi'
                                refetchData={refetchData}
                            />

                        ) : (
                            <div className='card empty-card-message'>
                                <p>{t('Geen informatie velden ingesteld')}</p>
                            </div>
                        )
                    ) : null;
                    break;
                // Add other tab components here
            }
        }

        return {
            label: t(tab.label),
            component,
            index
        };
    });

    return (
        <div>
            {tabComponents.length > 1 && (
                <ul className="card tabbar">
                    {tabComponents.map(({ label, index }) => (
                        <li key={index}
                            onClick={() => setActiveTab(index)}
                            className={index === activeTab ? "active" : ""}>
                            {t(label)}
                        </li>
                    ))}
                </ul>
            )}

            <div className="tabbar-content">
                {tabComponents[activeTab]?.component}
            </div>
        </div>
    );
};

export default TabBar;